import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import DialogBase from '../../../base/DialogBase/DialogBase';
import {
    useGetClientsShortQuery,
    useGetLibrariesLazyQuery,
    useGetDirectContentsLazyQuery,
    useGetLibraryQuery,
    useAssignContentsClassificationsMutation,
    useRegisterContentsMutation,
    useAssignContentsTagsMutation,
} from '../../../generated/graphql';
import styles from './CompareContentsDialog.module.css';
import { Loader2, Layers, CheckCircle2, PackageCheck, Package } from 'lucide-react';

const DEPRECATED_TAG_ID = '0f67def1-6879-41ba-81a0-6b28aa91eae2';

const GET_REGISTERED_CONTENT_IDS = gql`
    query getRegisteredContentIds($id: String) {
        Library(id: $id) {
            id
            registeredContentIds
        }
    }
`;

type ContentItem = {
    id?: string | null;
    name?: string | null;
    category?: string | null;
    classificationIds?: Array<string | null> | null;
};

type CompareContentsDialogProps = {
    onClose: () => void;
    visible: boolean;
    libraryId: string;
    currentContents: ContentItem[];
};

type ComparisonStatus = 'match' | 'only-current' | 'only-target';

type ComparisonRow = {
    name: string;
    category: string;
    status: ComparisonStatus;
    currentId?: string;
    targetId?: string;
    currentClassificationIds?: string[];
    targetClassificationIds?: string[];
};

const CompareContentsDialog = (props: CompareContentsDialogProps) => {
    const { visible, onClose, libraryId, currentContents } = props;

    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedLibraryId, setSelectedLibraryId] = useState('');
    const [statusFilter, setStatusFilter] = useState<ComparisonStatus | 'all'>('all');
    const [searchText, setSearchText] = useState('');

    // Fetch all pages state
    const [allCurrentContents, setAllCurrentContents] = useState<ContentItem[]>([]);
    const [targetContents, setTargetContents] = useState<ContentItem[]>([]);
    const [isFetchingTarget, setIsFetchingTarget] = useState(false);
    const [comparisonReady, setComparisonReady] = useState(false);
    const fetchingRef = useRef(false);

    // Apply classifications state
    const [isApplying, setIsApplying] = useState(false);
    const [applyProgress, setApplyProgress] = useState({ done: 0, total: 0 });
    const [applyResult, setApplyResult] = useState<{ success: number; skipped: number; failed: number } | null>(null);

    // Register state
    const [isRegistering, setIsRegistering] = useState(false);
    const [registerProgress, setRegisterProgress] = useState({ done: 0, total: 0 });
    const [registerResult, setRegisterResult] = useState<{ success: number; failed: number } | null>(null);
    const [registeredIds, setRegisteredIds] = useState<Set<string>>(new Set());
    const [preRegisteredIds, setPreRegisteredIds] = useState<Set<string>>(new Set());
    const [registeringId, setRegisteringId] = useState<string | null>(null);

    // Queries & mutations
    const clientsQuery = useGetClientsShortQuery({ fetchPolicy: 'cache-first' });
    const [fetchLibraries, librariesQuery] = useGetLibrariesLazyQuery({ fetchPolicy: 'cache-first' });
    const apolloClient = useApolloClient();
    const [fetchDirectContents] = useGetDirectContentsLazyQuery({ fetchPolicy: 'no-cache' });
    const currentLibraryQuery = useGetLibraryQuery({ variables: { id: libraryId } });
    const libraryName = currentLibraryQuery.data?.Library?.name || 'Current';
    const [assignClassifications] = useAssignContentsClassificationsMutation();
    const [registerContents] = useRegisterContentsMutation();
    const [assignTags] = useAssignContentsTagsMutation();

    const clients = useMemo(() => {
        const nodes = clientsQuery.data?.ClientList?.nodes || [];
        return [...nodes].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    }, [clientsQuery.data]);

    const libraries = useMemo(() => {
        const nodes = librariesQuery.data?.LibraryList?.nodes || [];
        return [...nodes]
            .filter(l => l?.id !== libraryId) // exclude current library
            .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    }, [librariesQuery.data, libraryId]);

    // When client changes, fetch libraries
    useEffect(() => {
        if (selectedClientId) {
            setSelectedLibraryId('');
            setComparisonReady(false);
            setTargetContents([]);
            fetchLibraries({ variables: { clientId: selectedClientId, id: '', contains: '' } });
        }
    }, [selectedClientId, fetchLibraries]);

    // When library is selected, fetch all contents from target
    const handleCompare = useCallback(async () => {
        if (!selectedLibraryId || fetchingRef.current) return;
        fetchingRef.current = true;
        setIsFetchingTarget(true);
        setComparisonReady(false);
        setAllCurrentContents([]);
        setTargetContents([]);
        setApplyResult(null);
        setRegisterResult(null);
        setRegisteredIds(new Set());
        setPreRegisteredIds(new Set());
        setRegisteringId(null);

        try {
            // Fetch all current library contents (all pages)
            let currentNodes: ContentItem[] = [];
            let currentCursor: string | null | undefined = undefined;
            let currentHasNext = true;
            while (currentHasNext) {
                const result: Awaited<ReturnType<typeof fetchDirectContents>> = await fetchDirectContents({
                    variables: {
                        libraryId: libraryId,
                        includeFileDownload: false,
                        after: currentCursor as string | undefined,
                    },
                });
                const nodes = result.data?.Library?.directContents?.nodes || [];
                currentNodes = [...currentNodes, ...nodes.filter(Boolean) as any[]];
                const pageInfo: { hasNextPage?: boolean; endCursor?: string | null } | null | undefined = result.data?.Library?.directContents?.pageInfo;
                currentHasNext = pageInfo?.hasNextPage ?? false;
                currentCursor = pageInfo?.endCursor;
            }
            setAllCurrentContents(currentNodes);

            // Fetch all target library contents (all pages)
            let allNodes: ContentItem[] = [];
            let cursor: string | null | undefined = undefined;
            let hasNext = true;
            while (hasNext) {
                const result: Awaited<ReturnType<typeof fetchDirectContents>> = await fetchDirectContents({
                    variables: {
                        libraryId: selectedLibraryId,
                        includeFileDownload: false,
                        after: cursor as string | undefined,
                    },
                });
                const nodes = result.data?.Library?.directContents?.nodes || [];
                allNodes = [...allNodes, ...nodes.filter(Boolean) as any[]];
                const pageInfo: { hasNextPage?: boolean; endCursor?: string | null } | null | undefined = result.data?.Library?.directContents?.pageInfo;
                hasNext = pageInfo?.hasNextPage ?? false;
                cursor = pageInfo?.endCursor;
            }

            // Fetch registeredContentIds directly from the current library
            const regResult = await apolloClient.query<{
                Library?: { id?: string | null; registeredContentIds?: Array<string | null> | null } | null;
            }>({
                query: GET_REGISTERED_CONTENT_IDS,
                fetchPolicy: 'network-only',
                variables: { id: libraryId },
            });
            const regIds = new Set<string>(
                (regResult.data?.Library?.registeredContentIds || []).filter(Boolean) as string[]
            );

            // Store the set of content IDs already registered in the current library.
            // In the row render, row.targetId is checked against this set.
            setPreRegisteredIds(regIds);

            setTargetContents(allNodes);
            setComparisonReady(true);
        } catch (err) {
            console.error('Failed to fetch target library contents:', err);
        } finally {
            setIsFetchingTarget(false);
            fetchingRef.current = false;
        }
    }, [selectedLibraryId, libraryId, fetchDirectContents, apolloClient]);

    // Compute comparison
    const comparison = useMemo((): ComparisonRow[] => {
        if (!comparisonReady) return [];

        // Build key sets: name|category (lowercased for case-insensitive matching)
        const makeKey = (name?: string | null, category?: string | null) =>
            `${(name || '').toLowerCase().trim()}|||${(category || '').toLowerCase().trim()}`;

        type MapEntry = { name: string; category: string; id?: string; classificationIds: string[] };

        const currentMap = new Map<string, MapEntry>();
        for (const c of allCurrentContents) {
            const key = makeKey(c.name, c.category);
            if (!currentMap.has(key)) {
                currentMap.set(key, {
                    name: c.name || '',
                    category: c.category || '',
                    id: c.id || undefined,
                    classificationIds: (c.classificationIds || []).filter(Boolean) as string[],
                });
            }
        }

        const targetMap = new Map<string, MapEntry>();
        for (const t of targetContents) {
            const key = makeKey(t.name, t.category);
            if (!targetMap.has(key)) {
                targetMap.set(key, {
                    name: t.name || '',
                    category: t.category || '',
                    id: t.id || undefined,
                    classificationIds: (t.classificationIds || []).filter(Boolean) as string[],
                });
            }
        }

        const rows: ComparisonRow[] = [];

        // Items in current library
        Array.from(currentMap.entries()).forEach(([key, item]) => {
            const target = targetMap.get(key);
            if (target) {
                rows.push({
                    name: item.name,
                    category: item.category,
                    status: 'match',
                    currentId: item.id,
                    targetId: target.id,
                    currentClassificationIds: item.classificationIds,
                    targetClassificationIds: target.classificationIds,
                });
            } else {
                rows.push({ name: item.name, category: item.category, status: 'only-current', currentId: item.id, currentClassificationIds: item.classificationIds });
            }
        });

        // Items only in target library
        Array.from(targetMap.entries()).forEach(([key, item]) => {
            if (!currentMap.has(key)) {
                rows.push({ name: item.name, category: item.category, status: 'only-target', targetId: item.id, targetClassificationIds: item.classificationIds });
            }
        });

        // Sort: matches first, then only-current, then only-target, then by name
        const statusOrder: Record<ComparisonStatus, number> = { 'match': 0, 'only-current': 1, 'only-target': 2 };
        rows.sort((a, b) => {
            const s = statusOrder[a.status] - statusOrder[b.status];
            if (s !== 0) return s;
            return a.name.localeCompare(b.name);
        });

        return rows;
    }, [comparisonReady, allCurrentContents, targetContents]);

    // Filtered rows
    const filteredRows = useMemo(() => {
        let rows = comparison;
        if (statusFilter !== 'all') {
            rows = rows.filter(r => r.status === statusFilter);
        }
        if (searchText.trim()) {
            const s = searchText.toLowerCase();
            rows = rows.filter(r =>
                r.name.toLowerCase().includes(s) ||
                r.category.toLowerCase().includes(s)
            );
        }
        return rows;
    }, [comparison, statusFilter, searchText]);

    // Summary stats
    const stats = useMemo(() => {
        const match = comparison.filter(r => r.status === 'match').length;
        const onlyCurrent = comparison.filter(r => r.status === 'only-current').length;
        const onlyTarget = comparison.filter(r => r.status === 'only-target').length;
        return { match, onlyCurrent, onlyTarget, total: comparison.length };
    }, [comparison]);

    // Apply classifications from current library matches to target library
    const matchesWithClassifications = useMemo(() => {
        return comparison.filter(r =>
            r.status === 'match' &&
            r.targetId &&
            r.currentClassificationIds &&
            r.currentClassificationIds.length > 0
        );
    }, [comparison]);

    const handleApplyClassifications = useCallback(async () => {
        if (isApplying || !selectedLibraryId || matchesWithClassifications.length === 0) return;
        setIsApplying(true);
        setApplyResult(null);
        const total = matchesWithClassifications.length;
        setApplyProgress({ done: 0, total });

        let success = 0;
        let skipped = 0;
        let failed = 0;

        for (let i = 0; i < matchesWithClassifications.length; i++) {
            const row = matchesWithClassifications[i];
            try {
                await assignClassifications({
                    variables: {
                        libraryId: selectedLibraryId,
                        ids: [row.targetId!],
                        classificationIds: row.currentClassificationIds!,
                        overwrite: false,
                    },
                });
                success++;
            } catch (err) {
                console.error(`Failed to apply classifications to ${row.name}:`, err);
                failed++;
            }
            setApplyProgress({ done: i + 1, total });
        }

        setApplyResult({ success, skipped, failed });
        setIsApplying(false);
    }, [isApplying, selectedLibraryId, matchesWithClassifications, assignClassifications]);

    // Register: matched items with both current and target IDs, excluding already-registered
    const registerableMatches = useMemo(() => {
        return comparison.filter(r =>
            r.status === 'match' &&
            r.currentId &&
            r.targetId &&
            !preRegisteredIds.has(r.targetId!)
        );
    }, [comparison, preRegisteredIds]);

    const handleRegisterSingle = useCallback(async (row: ComparisonRow) => {
        if (!row.currentId || !row.targetId || registeredIds.has(row.currentId) || preRegisteredIds.has(row.targetId)) return;
        setRegisteringId(row.currentId);
        try {
            // 1. Tag current library content as Deprecated (overwrite: false)
            await assignTags({
                variables: {
                    libraryId: libraryId,
                    ids: [row.currentId],
                    tagIds: [DEPRECATED_TAG_ID],
                    overwrite: false,
                },
            });
            // 2. Register the target content into the current library
            await registerContents({
                variables: {
                    id: libraryId,
                    contentIds: [row.targetId],
                },
            });
            setRegisteredIds(prev => new Set(prev).add(row.currentId!));
        } catch (err) {
            console.error(`Failed to register ${row.name}:`, err);
        } finally {
            setRegisteringId(null);
        }
    }, [libraryId, registeredIds, preRegisteredIds, assignTags, registerContents]);

    const handleRegisterAll = useCallback(async () => {
        const toRegister = registerableMatches.filter(r => r.currentId && !registeredIds.has(r.currentId!));
        if (isRegistering || toRegister.length === 0) return;
        setIsRegistering(true);
        setRegisterResult(null);
        const total = toRegister.length;
        setRegisterProgress({ done: 0, total });

        let success = 0;
        let failed = 0;

        for (let i = 0; i < toRegister.length; i++) {
            const row = toRegister[i];
            try {
                await assignTags({
                    variables: {
                        libraryId: libraryId,
                        ids: [row.currentId!],
                        tagIds: [DEPRECATED_TAG_ID],
                        overwrite: false,
                    },
                });
                await registerContents({
                    variables: {
                        id: libraryId,
                        contentIds: [row.targetId!],
                    },
                });
                setRegisteredIds(prev => new Set(prev).add(row.currentId!));
                success++;
            } catch (err) {
                console.error(`Failed to register ${row.name}:`, err);
                failed++;
            }
            setRegisterProgress({ done: i + 1, total });
        }

        setRegisterResult({ success, failed });
        setIsRegistering(false);
    }, [isRegistering, registerableMatches, registeredIds, libraryId, assignTags, registerContents]);

    const unregisteredCount = registerableMatches.filter(r => r.currentId && !registeredIds.has(r.currentId!)).length;

    const selectedLibrary = libraries.find(l => l?.id === selectedLibraryId);
    const selectedClient = clients.find(c => c?.id === selectedClientId);

    const statusLabel = (s: ComparisonStatus) => {
        switch (s) {
            case 'match': return 'In Both';
            case 'only-current': return `Only in Current`;
            case 'only-target': return `Only in Target`;
        }
    };

    const statusBadgeClass = (s: ComparisonStatus) => {
        switch (s) {
            case 'match': return styles.badgeMatch;
            case 'only-current': return styles.badgeOnlyCurrent;
            case 'only-target': return styles.badgeOnlyTarget;
        }
    };

    return (
        <DialogBase
            title="Compare Library Contents"
            type="Custom"
            visible={visible}
            onClose={onClose}
            width="900px"
            isLoading={clientsQuery.loading}
            className={styles.CompareContentsDialog}
            actions={
                <div className="flex items-center gap-2">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            }
        >
            {/* Step 1: Select client and library */}
            <div className="flex gap-3 mb-4">
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                        Client
                    </label>
                    <select
                        className="input-base w-full"
                        value={selectedClientId}
                        onChange={e => setSelectedClientId(e.target.value)}
                        style={{ cursor: 'pointer' }}
                    >
                        <option value="">Select a client…</option>
                        {clients.map(c => (
                            <option key={c?.id} value={c?.id || ''}>{c?.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                        Library
                    </label>
                    <select
                        className="input-base w-full"
                        value={selectedLibraryId}
                        onChange={e => setSelectedLibraryId(e.target.value)}
                        disabled={!selectedClientId || librariesQuery.loading}
                        style={{ cursor: 'pointer' }}
                    >
                        <option value="">
                            {librariesQuery.loading ? 'Loading libraries…' : 'Select a library…'}
                        </option>
                        {libraries.map(l => (
                            <option key={l?.id} value={l?.id || ''}>{l?.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-end">
                    <button
                        className="btn btn-primary"
                        disabled={!selectedLibraryId || isFetchingTarget}
                        onClick={handleCompare}
                    >
                        {isFetchingTarget ? (
                            <span className="flex items-center gap-1.5">
                                <Loader2 size={14} className="animate-spin" /> Loading…
                            </span>
                        ) : (
                            'Compare'
                        )}
                    </button>
                </div>
            </div>

            {/* Loading indicator */}
            {isFetchingTarget && (
                <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                    <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Fetching contents from <strong>{selectedLibrary?.name}</strong>…
                    </span>
                </div>
            )}

            {/* Results */}
            {comparisonReady && (
                <>
                    {/* Summary – click a card to filter */}
                    <div className={styles.summaryCards}>
                        <div
                            className={`${styles.summaryCard} ${statusFilter === 'match' ? styles.summaryCardActive : ''}`}
                            onClick={() => setStatusFilter(f => f === 'match' ? 'all' : 'match')}
                        >
                            <div className={styles.summaryCardValue}>{stats.match}</div>
                            <div className={styles.summaryCardLabel}>In Both Libraries</div>
                        </div>
                        <div
                            className={`${styles.summaryCard} ${statusFilter === 'only-current' ? styles.summaryCardActive : ''}`}
                            onClick={() => setStatusFilter(f => f === 'only-current' ? 'all' : 'only-current')}
                        >
                            <div className={styles.summaryCardValue} style={{ color: '#b45309' }}>{stats.onlyCurrent}</div>
                            <div className={styles.summaryCardLabel}>Only in {libraryName}</div>
                        </div>
                        <div
                            className={`${styles.summaryCard} ${statusFilter === 'only-target' ? styles.summaryCardActive : ''}`}
                            onClick={() => setStatusFilter(f => f === 'only-target' ? 'all' : 'only-target')}
                        >
                            <div className={styles.summaryCardValue} style={{ color: '#4338ca' }}>{stats.onlyTarget}</div>
                            <div className={styles.summaryCardLabel}>Only in {selectedLibrary?.name || 'Target'}</div>
                        </div>
                        <div
                            className={`${styles.summaryCard} ${statusFilter === 'all' ? styles.summaryCardActive : ''}`}
                            onClick={() => setStatusFilter('all')}
                        >
                            <div className={styles.summaryCardValue}>{stats.total}</div>
                            <div className={styles.summaryCardLabel}>Total Unique</div>
                        </div>
                    </div>

                    {/* Filters & Actions */}
                    <div className={styles.filterRow}>
                        <input
                            type="text"
                            className="input-base text-sm"
                            style={{ maxWidth: 260 }}
                            placeholder="Search by name or category…"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                        />
                        <button
                            className="btn btn-ghost text-sm"
                            disabled={isApplying || matchesWithClassifications.length === 0}
                            onClick={handleApplyClassifications}
                            title={`Apply classifications from ${libraryName} to ${matchesWithClassifications.length} matching content(s) in ${selectedLibrary?.name || 'target'} (overwrite: off)`}
                        >
                            {isApplying ? (
                                <span className="flex items-center gap-1.5">
                                    <Loader2 size={14} className="animate-spin" />
                                    {applyProgress.done}/{applyProgress.total}
                                </span>
                            ) : (
                                <span className="flex items-center gap-1.5">
                                    <Layers size={14} /> Apply Classifications ({matchesWithClassifications.length})
                                </span>
                            )}
                        </button>
                        <button
                            className="btn btn-ghost text-sm"
                            disabled={isRegistering || !!registeringId || unregisteredCount === 0}
                            onClick={handleRegisterAll}
                            title={`Tag current content as Deprecated and register target content into ${libraryName} for all ${unregisteredCount} matched item(s)`}
                        >
                            {isRegistering ? (
                                <span className="flex items-center gap-1.5">
                                    <Loader2 size={14} className="animate-spin" />
                                    {registerProgress.done}/{registerProgress.total}
                                </span>
                            ) : (
                                <span className="flex items-center gap-1.5">
                                    <PackageCheck size={14} /> Register All ({unregisteredCount})
                                </span>
                            )}
                        </button>
                        <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>
                            Showing {filteredRows.length} of {stats.total}
                        </span>
                    </div>

                    {/* Apply result banner */}
                    {applyResult && (
                        <div className="flex items-center gap-2 mb-3 px-3 py-2.5 rounded-md" style={{ backgroundColor: applyResult.failed > 0 ? '#991b1b' : '#166534', border: `1px solid ${applyResult.failed > 0 ? '#dc2626' : '#22c55e'}` }}>
                            <CheckCircle2 size={15} style={{ color: '#ffffff', flexShrink: 0 }} />
                            <span className="text-sm font-medium" style={{ color: '#ffffff' }}>
                                Classifications applied: <strong>{applyResult.success}</strong> succeeded
                                {applyResult.failed > 0 && <>, <strong>{applyResult.failed}</strong> failed</>}
                            </span>
                        </div>
                    )}

                    {/* Register result banner */}
                    {registerResult && (
                        <div className="flex items-center gap-2 mb-3 px-3 py-2.5 rounded-md" style={{ backgroundColor: registerResult.failed > 0 ? '#991b1b' : '#166534', border: `1px solid ${registerResult.failed > 0 ? '#dc2626' : '#22c55e'}` }}>
                            <PackageCheck size={15} style={{ color: '#ffffff', flexShrink: 0 }} />
                            <span className="text-sm font-medium" style={{ color: '#ffffff' }}>
                                Registered: <strong>{registerResult.success}</strong> succeeded
                                {registerResult.failed > 0 && <>, <strong>{registerResult.failed}</strong> failed</>}
                            </span>
                        </div>
                    )}

                    {/* Comparison table */}
                    <div style={{ maxHeight: '40vh', overflow: 'auto', borderRadius: 6, border: '1px solid var(--border-default)' }}>
                        <table className={styles.comparisonTable}>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Category</th>
                                    <th style={{ width: 140 }}>Status</th>
                                    <th style={{ width: 90 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>
                                            {comparison.length === 0 ? 'No contents to compare.' : 'No results match filters.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRows.map((row, i) => {
                                        const isMatch = row.status === 'match' && row.currentId && row.targetId;
                                        const wasPreRegistered = row.targetId ? preRegisteredIds.has(row.targetId) : false;
                                        const justRegistered = row.currentId ? registeredIds.has(row.currentId) : false;
                                        const isRowRegistering = registeringId === row.currentId;
                                        const rowOpacity = wasPreRegistered && !justRegistered ? 0.55 : 1;
                                        return (
                                            <tr key={`${row.name}-${row.category}-${i}`} style={{ opacity: rowOpacity }}>
                                                <td style={{ color: 'var(--text-primary)' }}>
                                                    {row.name}
                                                    {wasPreRegistered && !justRegistered && (
                                                        <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#78716c', color: '#ffffff' }}>Deprecated</span>
                                                    )}
                                                </td>
                                                <td style={{ color: 'var(--text-secondary)' }}>{row.category}</td>
                                                <td>
                                                    <span className={`${styles.badge} ${statusBadgeClass(row.status)}`}>
                                                        {statusLabel(row.status)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {isMatch && (
                                                        wasPreRegistered ? (
                                                            justRegistered ? (
                                                                <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#15803d' }}>
                                                                    <CheckCircle2 size={13} /> Done
                                                                </span>
                                                            ) : (
                                                                <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#78716c' }}>
                                                                    <PackageCheck size={13} /> Registered
                                                                </span>
                                                            )
                                                        ) : justRegistered ? (
                                                            <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#15803d' }}>
                                                                <CheckCircle2 size={13} /> Done
                                                            </span>
                                                        ) : (
                                                            <button
                                                                className="btn btn-ghost text-xs py-0.5 px-2"
                                                                disabled={isRegistering || !!registeringId}
                                                                onClick={(e) => { e.stopPropagation(); handleRegisterSingle(row); }}
                                                                title="Tag as Deprecated & register target content"
                                                            >
                                                                {isRowRegistering ? (
                                                                    <Loader2 size={12} className="animate-spin" />
                                                                ) : (
                                                                    <span className="flex items-center gap-1">
                                                                        <Package size={12} /> Register
                                                                    </span>
                                                                )}
                                                            </button>
                                                        )
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </DialogBase>
    );
};

export default CompareContentsDialog;
